/* global axios */
import ApiClient from './ApiClient';

/**
 * The ticket engine's type catalogue and its settings writes.
 *
 * Its own resource rather than a method on `supportCases`, because that client
 * is scoped to `/support_cases` and these live beside it. Reads answer the
 * permission scope's set (never empty for a flagged-on account, which is
 * bootstrapped with one type on first request); the writes are administrator
 * only and the server says so.
 *
 * Not paginated — a workspace has a handful of types — so the read is the whole
 * set in one `{ payload }`. Every stage write answers with the whole type,
 * because the rail is what changed and the editor renders the rail.
 */
class CaseTypesAPI extends ApiClient {
  constructor() {
    super('case_types', { accountScoped: true });
  }

  get() {
    return axios.get(this.url);
  }

  createStage(caseTypeId, stage) {
    return axios.post(`${this.url}/${caseTypeId}/stages`, stage);
  }

  updateStage(caseTypeId, stageId, stage) {
    return axios.patch(`${this.url}/${caseTypeId}/stages/${stageId}`, stage);
  }

  // `targetCaseStageId` is required only when the stage still carries open
  // tickets; the server refuses without it rather than guessing where they go.
  archiveStage(caseTypeId, stageId, { targetCaseStageId } = {}) {
    return axios.delete(`${this.url}/${caseTypeId}/stages/${stageId}`, {
      params: targetCaseStageId
        ? { target_case_stage_id: targetCaseStageId }
        : {},
    });
  }

  // The whole rail in one call: a half-applied drag is not a state the editor
  // should be able to produce.
  reorderStages(caseTypeId, stageIds) {
    return axios.post(`${this.url}/${caseTypeId}/stages/reorder`, {
      stage_ids: stageIds,
    });
  }

  createFieldDefinition(caseTypeId, definition) {
    return axios.post(
      `${this.url}/${caseTypeId}/field_definitions`,
      definition
    );
  }

  // The key is written once, at creation: it addresses this field's value inside
  // every ticket already filed under the type, so the server ignores it here.
  updateFieldDefinition(caseTypeId, definitionId, definition) {
    return axios.patch(
      `${this.url}/${caseTypeId}/field_definitions/${definitionId}`,
      definition
    );
  }

  // Archival, not deletion: the values collected under the key stay on their
  // tickets, the field simply stops being asked for.
  archiveFieldDefinition(caseTypeId, definitionId) {
    return axios.delete(
      `${this.url}/${caseTypeId}/field_definitions/${definitionId}`
    );
  }

  reorderFieldDefinitions(caseTypeId, definitionIds) {
    return axios.post(`${this.url}/${caseTypeId}/field_definitions/reorder`, {
      field_definition_ids: definitionIds,
    });
  }
}

export default new CaseTypesAPI();
